/**
 * settings-tab.tsx — Tab "Cài đặt tổ chức": tên + ghi chú nội bộ, thông tin công ty (SĐT, email liên hệ, MST…),
 * hạn mức AI, khoá / mở tổ chức (khoá = đăng xuất mọi phiên), khởi tạo lại vai trò mặc định, xoá tổ chức.
 */
import { useEffect, useState } from 'react'
import { Loader2, Lock, RotateCcw, Save, Unlock } from 'lucide-react'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch, Textarea } from '@/components/ui/misc'
import { ErrorState, Loading } from '@/components/shared/feedback'
import { platformApiError } from '@/lib/platform-client'
import {
  COMPANY_KEYS, useOrgAiQuota, useOrgOverview, useReseedRoles, useSaveOrgAiQuota, useSaveOrgCompany, useSetOrgSuspended,
  useUpdateOrgSettings, type CompanyKey,
} from '@/hooks/use-platform-org-manage'
import { COMPANY_FIELDS } from '@/pages/settings/settings-utils'
import { ReasonDialog } from './reason-dialog'
import { DeleteOrgCard } from './delete-org'
import { companyChanges, companyFieldError, emptyCompany, type CompanyForm } from './profile-helpers'

export function SettingsTab({ orgId }: { orgId: string }) {
  const { data, isLoading, isError } = useOrgOverview(orgId)
  if (isLoading) return <Loading />
  if (isError || !data) return <ErrorState message="Không tải được cài đặt tổ chức." />
  if (data.org.deletedAt) {
    return <p className="text-sm text-muted-foreground">Tổ chức đã bị xoá — khôi phục ở đầu trang để sửa cài đặt.</p>
  }
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <InfoCard orgId={orgId} name={data.org.name} adminNotes={data.org.adminNotes} />
      <CompanyCard orgId={orgId} saved={data.company} />
      <AiQuotaCard orgId={orgId} />
      <SuspendCard orgId={orgId} suspended={data.org.status === 'suspended'} />
      <RolesCard orgId={orgId} systemRoles={data.stats.systemRoles} />
      <DeleteOrgCard orgId={orgId} orgName={data.org.name} purgeAfterDays={data.org.purgeAfterDays ?? 30} />
    </div>
  )
}

/** Thông tin công ty — cùng các ô khách tự nhập ở Cài đặt → Thông tin công ty (khoá company.*). */
function CompanyCard({ orgId, saved }: { orgId: string; saved?: Partial<CompanyForm> }) {
  const m = useSaveOrgCompany(orgId)
  const [form, setForm] = useState<CompanyForm>(() => ({ ...emptyCompany(), ...saved }))
  const [confirm, setConfirm] = useState(false)
  useEffect(() => setForm({ ...emptyCompany(), ...saved }), [saved])
  const changes = companyChanges(form, saved)
  const errors = COMPANY_KEYS.map((k) => [k, companyFieldError(k, form[k])] as const).filter(([, e]) => e)
  const dirty = Object.keys(changes).length > 0

  return (
    <Card>
      <CardHeader><CardTitle>Thông tin công ty</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">Khách thấy và sửa được ở Cài đặt → Thông tin công ty.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {COMPANY_FIELDS.map((f) => {
            const key = f.key as CompanyKey
            const err = companyFieldError(key, form[key])
            return (
              <div key={key} className={key === 'company.address' ? 'space-y-1.5 sm:col-span-2' : 'space-y-1.5'}>
                <Label htmlFor={`co-${key}`}>{f.label}</Label>
                <Input
                  id={`co-${key}`} type={f.type || 'text'} placeholder={f.placeholder} value={form[key]}
                  onChange={(e) => setForm((s) => ({ ...s, [key]: e.target.value }))}
                />
                {err && <p className="text-xs text-destructive">{err}</p>}
              </div>
            )
          })}
        </div>
        <Button onClick={() => setConfirm(true)} disabled={!dirty || errors.length > 0}>
          <Save className="h-4 w-4" /> Lưu thông tin công ty
        </Button>
      </CardContent>
      <ReasonDialog
        open={confirm} onOpenChange={setConfirm} title="Lưu thông tin công ty" reasonRequired={false}
        confirmLabel="Lưu" pending={m.isPending}
        onConfirm={async (reason) => {
          try {
            await m.mutateAsync({ values: changes, reason: reason || undefined })
            toast.success('Đã lưu thông tin công ty')
            setConfirm(false)
          } catch (err) { toast.error(platformApiError(err)) }
        }}
      />
    </Card>
  )
}

function InfoCard({ orgId, name, adminNotes }: { orgId: string; name: string; adminNotes: string | null }) {
  const m = useUpdateOrgSettings(orgId)
  const [form, setForm] = useState({ name, adminNotes: adminNotes ?? '' })
  const [confirm, setConfirm] = useState(false)
  useEffect(() => setForm({ name, adminNotes: adminNotes ?? '' }), [name, adminNotes])
  const dirty = form.name.trim() !== name || (form.adminNotes.trim() || null) !== (adminNotes || null)

  return (
    <Card>
      <CardHeader><CardTitle>Thông tin tổ chức</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="org-name">Tên tổ chức</Label>
          <Input id="org-name" value={form.name} maxLength={200} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="org-notes">Ghi chú nội bộ (chỉ Platform thấy)</Label>
          <Textarea id="org-notes" rows={4} maxLength={5000} value={form.adminNotes} onChange={(e) => setForm((f) => ({ ...f, adminNotes: e.target.value }))} />
        </div>
        <Button onClick={() => setConfirm(true)} disabled={!dirty || !form.name.trim()}>
          <Save className="h-4 w-4" /> Lưu
        </Button>
      </CardContent>
      <ReasonDialog
        open={confirm} onOpenChange={setConfirm} title="Lưu thông tin tổ chức" reasonRequired={false}
        confirmLabel="Lưu" pending={m.isPending}
        onConfirm={async (reason) => {
          try {
            await m.mutateAsync({
              ...(form.name.trim() !== name ? { name: form.name.trim() } : {}),
              ...((form.adminNotes.trim() || null) !== (adminNotes || null) ? { adminNotes: form.adminNotes.trim() || null } : {}),
              reason: reason || undefined,
            })
            toast.success('Đã lưu thông tin tổ chức')
            setConfirm(false)
          } catch (err) { toast.error(platformApiError(err)) }
        }}
      />
    </Card>
  )
}

/**
 * Khoá / trần AI do PLATFORM đặt (khung ba tầng): `platformAiDisabled`, `platformMaxDaily`. Tổ chức không sửa được hai giá
 * trị này; cài đặt riêng của tổ chức (bật AI, tạm dừng toàn bộ, lượt / ngày) chỉ hiện để tham khảo.
 */
function AiQuotaCard({ orgId }: { orgId: string }) {
  const { data, isLoading } = useOrgAiQuota(orgId)
  const m = useSaveOrgAiQuota(orgId)
  const [cap, setCap] = useState('')
  const [locked, setLocked] = useState(false)
  useEffect(() => {
    if (data) {
      setCap(data.platformMaxDaily != null ? String(data.platformMaxDaily) : '')
      setLocked(!!data.platformAiDisabled)
    }
  }, [data])

  async function save() {
    const text = cap.trim()
    const n = text === '' ? null : Number(text)
    if (n !== null && (!Number.isInteger(n) || n < 0)) return toast.error('Trần phải là số nguyên ≥ 0 (để trống = không đặt)')
    try {
      await m.mutateAsync({ platformAiDisabled: locked, platformMaxDaily: n })
      toast.success('Đã lưu khoá / trần AI của Platform')
    } catch (err) { toast.error(platformApiError(err)) }
  }

  return (
    <Card>
      <CardHeader><CardTitle>Khoá & trần AI (Platform)</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? <Loading /> : (
          <>
            <p className="text-xs text-muted-foreground">
              Do Platform áp — tổ chức <b>không sửa được</b>. Không đè cài đặt riêng của tổ chức: AI chỉ chạy khi tổ chức bật
              và Platform không khoá; trần lượt thực tế là mức thấp hơn giữa trần Platform và trần tổ chức tự đặt.
            </p>
            <label className="flex items-center justify-between gap-3 text-sm">
              <span>
                <span className="font-medium">Khoá AI của tổ chức</span>
                <span className="block text-xs text-muted-foreground">Nhân viên AI ngừng trả lời và gợi ý; tổ chức thấy thông báo “eCDP đang khoá AI”.</span>
              </span>
              <Switch checked={locked} onCheckedChange={setLocked} aria-label="Khoá AI của tổ chức" />
            </label>
            <div className="space-y-1.5">
              <Label htmlFor="ai-platform-max">Trần lượt / ngày do Platform đặt</Label>
              <Input id="ai-platform-max" type="number" min={0} className="w-36" placeholder="Không đặt" value={cap} onChange={(e) => setCap(e.target.value)} />
            </div>
            <div className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              Cài đặt của tổ chức (chỉ xem): AI {data?.enabled === false ? 'đang tắt' : 'đang bật'}
              {data?.autoReplyEnabled === false ? ' · đang tạm dừng toàn bộ' : ''}
              {data?.maxDaily != null ? ` · ${data.maxDaily.toLocaleString('vi-VN')} lượt / ngày` : ' · chưa dùng AI lần nào'}
            </div>
            <Button onClick={save} disabled={m.isPending}>
              {m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Lưu
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function SuspendCard({ orgId, suspended }: { orgId: string; suspended: boolean }) {
  const m = useSetOrgSuspended(orgId)
  const [open, setOpen] = useState(false)
  return (
    <Card>
      <CardHeader><CardTitle>Khoá tổ chức</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm">{suspended ? 'Tổ chức đang bị khoá.' : 'Tổ chức đang hoạt động.'}</p>
        <p className="text-xs text-muted-foreground">
          Khoá chặn mọi nhân viên đăng nhập, thu hồi toàn bộ phiên (refresh token) và ngắt kết nối realtime.
          Token truy cập đang cầm còn hiệu lực tối đa 30 phút.
        </p>
        <Button variant={suspended ? 'default' : 'destructive'} onClick={() => setOpen(true)}>
          {suspended ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
          {suspended ? 'Mở khoá tổ chức' : 'Khoá tổ chức'}
        </Button>
      </CardContent>
      <ReasonDialog
        open={open} onOpenChange={setOpen}
        title={suspended ? 'Mở khoá tổ chức' : 'Khoá tổ chức'}
        description={suspended ? 'Nhân viên đăng nhập lại được.' : 'Mọi nhân viên bị đăng xuất và không đăng nhập được cho tới khi mở khoá.'}
        confirmLabel={suspended ? 'Mở khoá' : 'Khoá'} destructive={!suspended} pending={m.isPending}
        onConfirm={async (reason) => {
          try {
            const r = await m.mutateAsync({ suspended: !suspended, reason })
            toast.success(suspended ? 'Đã mở khoá tổ chức' : `Đã khoá tổ chức · thu hồi ${r.revokedSessions} phiên`)
            setOpen(false)
          } catch (err) { toast.error(platformApiError(err)) }
        }}
      />
    </Card>
  )
}

function RolesCard({ orgId, systemRoles }: { orgId: string; systemRoles: number }) {
  const m = useReseedRoles(orgId)
  const [open, setOpen] = useState(false)
  return (
    <Card>
      <CardHeader><CardTitle>Vai trò mặc định</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm">Tổ chức có {systemRoles}/4 vai trò gốc (Chủ sở hữu, Quản trị, Quản lý, Nhân viên).</p>
        <p className="text-xs text-muted-foreground">
          Tạo vai trò gốc còn thiếu và gán vai trò cho nhân viên chưa gán. Không ghi đè tên/quyền tổ chức đã chỉnh
          (trừ Chủ sở hữu luôn đủ quyền).
        </p>
        <Button variant="outline" onClick={() => setOpen(true)}><RotateCcw className="h-4 w-4" /> Khởi tạo lại vai trò mặc định</Button>
      </CardContent>
      <ReasonDialog
        open={open} onOpenChange={setOpen} title="Khởi tạo lại vai trò mặc định" reasonRequired={false}
        confirmLabel="Khởi tạo" pending={m.isPending}
        onConfirm={async (reason) => {
          try {
            const r = await m.mutateAsync({ reason: reason || undefined })
            toast.success(`Đã khởi tạo: ${r.roles} vai trò gốc, gán ${r.usersLinked} nhân viên`)
            setOpen(false)
          } catch (err) { toast.error(platformApiError(err)) }
        }}
      />
    </Card>
  )
}
